import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import type Owner from '@ember/owner';
import type NetworkStatus from './network-status';
import {
  addSubmission,
  countSubmissions,
  getAllSubmissions,
  removeSubmission,
  clearAllSubmissions,
  sha256Hex,
} from '../utils/offline-db';
import { apiFetch } from '../fetch';

const MAX_QUEUE_SIZE = 50;

/**
 * Offline Queue Service
 *
 * Manages a persistent IndexedDB queue of submissions (URLs and images)
 * that were created while offline. Automatically syncs when connectivity
 * returns.
 */
export default class OfflineQueue extends Service {
  @service declare networkStatus: NetworkStatus;

  @tracked pendingCount = 0;
  @tracked isSyncing = false;

  constructor(owner: Owner) {
    super(owner);
    void this.loadCount();
    this.networkStatus.onReconnect(() => {
      void this.processQueue();
    });
    void this.setupAppResume();
  }

  /**
   * Enqueue a URL submission for later sync. Returns true if added,
   * false if it was a duplicate or queue is full.
   */
  async enqueueUrl(url: string): Promise<boolean> {
    if (this.pendingCount >= MAX_QUEUE_SIZE) return false;

    const added = await addSubmission({
      type: 'url',
      url,
      dedupeKey: url,
      createdAt: Date.now(),
    });

    if (added) {
      this.pendingCount++;
    }
    return added;
  }

  /**
   * Enqueue an image submission for later sync. The full blob is stored
   * in IndexedDB so it survives content:// URI revocation on Android.
   * Returns true if added, false if duplicate or queue full.
   */
  async enqueueImage(blob: Blob, mimeType: string): Promise<boolean> {
    if (this.pendingCount >= MAX_QUEUE_SIZE) return false;

    const buffer = await blob.arrayBuffer();
    const hash = await sha256Hex(buffer);

    const added = await addSubmission({
      type: 'image',
      blob: buffer,
      mimeType,
      dedupeKey: `image:${hash}`,
      createdAt: Date.now(),
    });

    if (added) {
      this.pendingCount++;
    }
    return added;
  }

  /**
   * Process all queued submissions. Called automatically on reconnect
   * and on app resume with connectivity.
   */
  async processQueue(): Promise<{ synced: number; failed: number }> {
    if (this.isSyncing) return { synced: 0, failed: 0 };

    const session = getOwner(this)?.lookup('service:session');
    if (!session?.isAuthenticated) return { synced: 0, failed: 0 };

    this.isSyncing = true;

    try {
      const entries = await getAllSubmissions();
      let synced = 0;
      let failed = 0;

      for (const entry of entries) {
        try {
          if (entry.type === 'url' && entry.url) {
            await this.syncUrl(entry.url, session.authHeader);
          } else if (entry.type === 'image' && entry.blob) {
            await this.syncImage(
              entry.blob,
              entry.mimeType ?? 'image/jpeg',
              session.authHeader
            );
          }

          if (entry.id !== undefined) {
            await removeSubmission(entry.id);
            synced++;
          }
        } catch {
          failed++;
          // API error — stop processing, retry later
          break;
        }
      }

      this.pendingCount = await countSubmissions();

      const notifications = getOwner(this)?.lookup('service:notifications');
      if (synced > 0) {
        notifications?.success(
          `Synced ${synced} offline submission${synced > 1 ? 's' : ''}`
        );
      }
      if (failed > 0 && synced === 0) {
        notifications?.error('Could not reach the server — will retry later');
      }

      return { synced, failed };
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Remove all pending submissions.
   */
  async clearQueue(): Promise<void> {
    await clearAllSubmissions();
    this.pendingCount = 0;
  }

  private async loadCount(): Promise<void> {
    try {
      this.pendingCount = await countSubmissions();
    } catch {
      this.pendingCount = 0;
    }
  }

  private async syncUrl(
    url: string,
    authHeader: string | undefined
  ): Promise<void> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/vnd.api+json',
    };
    if (authHeader) headers['Authorization'] = authHeader;

    const response = await apiFetch('/api/submissions', {
      method: 'POST',
      headers,
      body: JSON.stringify({ url }),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`Sync failed (${response.status}): ${text}`);
    }
  }

  private async syncImage(
    buffer: ArrayBuffer,
    mimeType: string,
    authHeader: string | undefined
  ): Promise<void> {
    const ext = mimeType.split('/')[1] ?? 'jpg';
    const file = new File([buffer], `offline-image.${ext}`, {
      type: mimeType,
    });

    const formData = new FormData();
    formData.append('image', file);

    const headers: Record<string, string> = {};
    if (authHeader) headers['Authorization'] = authHeader;

    const response = await apiFetch('/api/submissions/image', {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`Image sync failed (${response.status}): ${text}`);
    }
  }

  private async setupAppResume(): Promise<void> {
    try {
      const { Capacitor } = await import('@capacitor/core');
      if (!Capacitor.isNativePlatform()) return;

      const { App } = await import('@capacitor/app');
      await App.addListener('appStateChange', (state) => {
        if (state.isActive && this.networkStatus.isOnline) {
          void this.processQueue();
        }
      });
    } catch {
      // Not a Capacitor environment — no app resume listener needed
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'offline-queue': OfflineQueue;
  }
}
